import { SB } from '@play-co/replicant';
import { tuple } from 'src/replicant/utils/types';
import { SpeciesType, getPetAssetKey, speciesChatbot } from './species';
import {
  createFlexMessageTemplate,
  createFlexContainer,
  createOpenGameAction,
} from './utils';
import { FlexComponent } from './types';
import { buildSender } from '../sender';

/**
 * Assets for state messages
 */
export const chatAssets = {
  'background/chat/play-sad/en': 'private/chatbot/chat/play_sad_en.png',
  'background/chat/play-sad/ja': 'private/chatbot/chat/play_sad_jp.png',
  'background/chat/play-happy/en': 'private/chatbot/chat/play_happy_en.png',
  'background/chat/play-happy/ja': 'private/chatbot/chat/play_happy_jp.png',
  'background/chat/rescue/en': 'private/chatbot/chat/rescue_en.png',
  'background/chat/rescue/ja': 'private/chatbot/chat/rescue_jp.png',
  'background/chat/dead/en': 'private/chatbot/chat/dead_en.png',
  'background/chat/dead/ja': 'private/chatbot/chat/dead_jp.png',
  'background/chat/error/en': 'private/chatbot/chat/error_en.png',
  'background/chat/error/ja': 'private/chatbot/chat/error_jp.png',
  'background/chat/sleeping/en': 'private/chatbot/chat/sleeping_en.png',
  'background/chat/sleeping/ja': 'private/chatbot/chat/sleeping_jp.png',
};

const statusVariants = tuple(
  'evolving',
  'sick',
  'toothDecay',
  'scared',
  'asleep',
  'dying',
  'dead',
  'hadVisitor',
  'wantToGoOut',
  'daycare',
  'very_dirty',
  'very_hungry',
  'very_lonely',
  'very_pee',
  'very_sad',
  'dirty',
  'hungry',
  'lonely',
  'pee',
  'sad',
  'happy',
  'normal',
);

type statusType = (typeof statusVariants)[number];

// Species mood to status message map
const statusToMood: Record<statusType, SpeciesType> = {
  dead: 'verysad',
  dying: 'dying',
  dirty: 'sad',
  hungry: 'sad',
  lonely: 'sad',
  evolving: 'normal',
  scared: 'sad',
  sick: 'sad',
  very_pee: 'verysad',
  toothDecay: 'sad',
  pee: 'sad',
  very_dirty: 'verysad',
  very_hungry: 'verysad',
  very_lonely: 'verysad',
  wantToGoOut: 'normal',
  asleep: 'normal',
  hadVisitor: 'happy',
  daycare: 'happy',
  very_sad: 'verysad',
  sad: 'sad',
  happy: 'happy',
  normal: 'normal',
};

// Add here different bottom offset for type, default offset 32%
const bottomOffset: Partial<Record<statusType, string>> = {
  hungry: '10%',
  pee: '11%',
};

// Supported languages
const languages = ['en', 'ja'] as const;
const languagesTuple = tuple(...languages);
type LanguagesType = (typeof languagesTuple)[number];

// Chat message type
const chatMessageType = [
  'rescue',
  'play-happy',
  'play-sad',
  'dead',
  'error',
  'sleeping',
] as const;
const chatMessageTuple = tuple(...chatMessageType);
export type ChatbotChatMessageType = (typeof chatMessageType)[number];

// Alt text localization, it used for line notification popups
const altTextLocalization = (
  name: string,
): Record<LanguagesType, Record<ChatbotChatMessageType, string>> => ({
  en: {
    rescue: `Help`,
    'play-happy': `${name} wants to play`,
    'play-sad': `${name} wants to play`,
    dead: `…`,
    error: 'Oops, something went wrong',
    sleeping: 'Hey I am sleeping!',
  },
  ja: {
    rescue: `助けて。。`,
    'play-happy': `ねねね！あそぼ！`,
    'play-sad': `ねねね！あそぼ！`,
    dead: `…`,
    error: 'エラーが発生しました',
    sleeping: 'むにゃむにゃ…',
  },
});

/**
 * Create chatbot message
 *
 * https://developers.line.biz/en/docs/messaging-api/flex-message-elements/
 * https://developers.line.biz/en/docs/messaging-api/using-flex-messages/
 *
 * Tools for generation of messages in flex format:
 * https://developers.line.biz/flex-simulator/
 */
export const chatMessage = createFlexMessageTemplate(
  SB.object({
    messageType: SB.tuple(chatMessageTuple),
    petName: SB.string(),
    species: SB.tuple(speciesChatbot),
    locale: SB.tuple(languagesTuple),
    status: SB.tuple(statusVariants),
  }),
  (data, payload, api) => {
    const altText = altTextLocalization(data.petName)[data.locale][
      data.messageType
    ];
    const mood = statusToMood[data.status];
    const icon = api.getAssetPath(getPetAssetKey(data.species, mood));
    const normal = api.getAssetPath(getPetAssetKey(data.species));
    const bottom = bottomOffset[data.status] ?? '10%';
    const contents: FlexComponent[] = [];

    // Main image
    contents.push({
      type: 'image',
      url: api.getAssetPath(
        `background/chat/${data.messageType}/${data.locale}`,
      ),
      size: 'full',
      aspectMode: 'cover',
      aspectRatio: '1.51:1',
      gravity: 'center',
    });

    // Pet image
    if (data.messageType !== 'error')
      contents.push({
        type: 'box',
        layout: 'vertical',
        position: 'absolute',
        width: '100%',
        height: '100%',
        offsetBottom: bottom,
        alignItems: 'flex-start',
        justifyContent: 'flex-end',
        offsetStart: '5%',
        contents: [
          {
            type: 'box',
            layout: 'vertical',
            contents: [
              {
                type: 'image',
                url: icon,
                aspectRatio: '286:213', // Use fixed aspect ratio for pet icon.
                size: 'full',
              },
            ],
            width: '35%',
            height: '45%',
          },
        ],
      });

    const template = createFlexContainer(altText, {
      styles: {
        body: {
          backgroundColor: '#8CABD9', // Light blue, like chat BG, transparent image doesn't work here
        },
      },
      type: 'bubble',
      size: 'giga',
      action: createOpenGameAction(altText, payload),
      body: {
        paddingAll: '0px',
        type: 'box',
        layout: 'vertical',
        contents,
      },
    });

    // Add sender to message
    template['sender'] = buildSender({ name: data.petName, iconUrl: normal });

    return template;
  },
);
